import { useState, useRef, useEffect } from "react";
import { getPortfolioMapping, savePortfolioMappings } from "../utils/portfolioClientMap";
import { SOURCE_TYPE } from "../utils/dataSource";

// הפרסר נטען דינמית — ‎xlsx כבד ואין סיבה שיישב ב-bundle הראשי
const loadParser = () => import("../parsers/managedPortfolioParser");

const fmt = n => (n || 0).toLocaleString("he-IL", { maximumFractionDigits: 0 });

// ── התאמת לקוחות ─────────────────────────────────────────────

// ת"ז: השוואה אחרי הסרת אפסים מובילים (כמו MigdalImport)
function findByTaz(taz, holdingsClients) {
  if (!taz) return null;
  const searchTaz = String(Number(taz));
  if (searchTaz === "NaN") return null;
  return holdingsClients.find(c =>
    c.clientId === taz ||
    (c.clientId && String(Number(c.clientId)) === searchTaz)
  ) || null;
}

// דמיון שמות: טוקנים בעברית באורך 2+ (מסנן "ק4", "ו/או", אותיות בודדות)
function nameTokens(s) {
  return String(s || "")
    .replace(/["'״׳\-,.()/]/g, " ")
    .split(/\s+/)
    .filter(t => t.length >= 2 && /[א-ת]/.test(t) && t !== "או");
}

function suggestByName(accountName, holdingsClients) {
  const tokens = nameTokens(accountName);
  if (!tokens.length) return null;
  let best = null, bestScore = 0;
  for (const c of holdingsClients) {
    const cTokens = nameTokens(c.clientName);
    if (!cTokens.length) continue;
    const overlap = cTokens.filter(t => tokens.includes(t)).length;
    const score = overlap / Math.max(cTokens.length, 1);
    if (overlap >= 1 && score > bestScore) { best = c; bestScore = score; }
  }
  // דורש לפחות 2 טוקנים משותפים או כיסוי מלא של שם הלקוח
  if (best) {
    const overlap = nameTokens(best.clientName).filter(t => tokens.includes(t)).length;
    if (overlap >= 2 || bestScore >= 1) return best;
  }
  return null;
}

// חודש ברירת-מחדל: תאריך מהקובץ → דפוס בשם הקובץ → ריק
function defaultMonth(reportDate, fileName) {
  if (reportDate) return reportDate.slice(0, 7); // YYYY-MM
  const name = String(fileName || "");
  let m = name.match(/(\d{1,2})[._\- ](\d{4})/);           // 4.2026
  if (m) return `${m[2]}-${m[1].padStart(2, "0")}`;
  m = name.match(/^(\d{2})(\d{2})(\d{2})(?!\d)/);          // 300426 = DDMMYY
  if (m) return `20${m[3]}-${m[2]}`;
  m = name.match(/^(\d{2})(\d{2})(\d{4})/);                // 30042026 = DDMMYYYY
  if (m) return `${m[3]}-${m[2]}`;
  m = name.match(/(\d{1,2})\.(\d{2})(?!\d)/);              // 3.26 = M.YY
  if (m) return `20${m[2]}-${m[1].padStart(2, "0")}`;
  return "";
}

// שיוך ראשוני לכל חשבון: מיפוי שמור (לפי בית ההשקעות הנבחר) → ת"ז → הצעת שם
function initialMatch(account, houseKey, holdingsClients) {
  if (houseKey) {
    const mapped = getPortfolioMapping(houseKey, account.portfolioNum);
    if (mapped) {
      const c = holdingsClients.find(cl => cl.clientId === mapped.clientId);
      if (c) return { clientId: c.clientId, clientName: c.clientName, how: "mapped" };
    }
  }
  const byTaz = findByTaz(account.taz, holdingsClients);
  if (byTaz) return { clientId: byTaz.clientId, clientName: byTaz.clientName, how: "taz" };
  const byName = suggestByName(account.accountName, holdingsClients);
  if (byName) return { clientId: byName.clientId, clientName: byName.clientName, how: "name" };
  return { clientId: "", clientName: "", how: null };
}

const HOW_LABEL = {
  mapped: { text: "שויך אוטומטית (זיהוי קודם)", color: "#059669" },
  taz:    { text: "שויך לפי ת\"ז", color: "#059669" },
  name:   { text: "הצעה לפי דמיון שם — אשר או שנה", color: "#d97706" },
};

// ── בורר לקוח עם חיפוש ───────────────────────────────────────
// כפתור שנראה כמו select; לחיצה פותחת חלונית עם שדה חיפוש (שם או ת"ז)
// ורשימה מסוננת. value: clientId | "" ; createNew: יצירת לקוח חדש (מור).
function ClientPicker({ value, createNew, taz, clients, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const onDoc = e => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selected = clients.find(c => c.clientId === value);
  const label = createNew
    ? `🆕 לקוח חדש (ת"ז ${taz})`
    : selected
    ? `${selected.clientName} (${selected.clientId})`
    : "— ללא שיוך (דלג) —";

  const norm = s => String(s || "").replace(/["'״׳\-]/g, "").toLowerCase();
  const nq = norm(q.trim());
  const filtered = nq
    ? clients.filter(c => norm(c.clientName).includes(nq) || String(c.clientId).includes(nq))
    : clients;
  const shown = filtered.slice(0, 50);

  const pick = patch => { onPick(patch); setOpen(false); setQ(""); };

  const itemStyle = { padding: "6px 10px", fontSize: 12, cursor: "pointer", borderBottom: "1px solid #f1f5f9" };

  return (
    <div ref={boxRef} style={{ position: "relative", minWidth: 200, maxWidth: 280 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", textAlign: "right", padding: "4px 8px", fontSize: 12,
          borderRadius: 6, border: "1px solid #cbd5e1", background: "white",
          cursor: "pointer", color: value || createNew ? "#0f172a" : "#94a3b8",
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        <span style={{ fontSize: 9, color: "#94a3b8", flexShrink: 0 }}>▼</span>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "100%", right: 0, left: 0, zIndex: 50,
          background: "white", border: "1px solid #cbd5e1", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)", marginTop: 2, overflow: "hidden",
        }}>
          <input
            ref={searchRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Escape") setOpen(false);
              if (e.key === "Enter" && shown.length === 1) {
                pick({ createNew: false, clientId: shown[0].clientId, clientName: shown[0].clientName });
              }
            }}
            placeholder='🔍 חיפוש לפי שם או ת"ז…'
            style={{
              width: "100%", boxSizing: "border-box", padding: "7px 10px", fontSize: 12,
              border: "none", borderBottom: "1px solid #e2e8f0", outline: "none",
            }}
          />
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            <div style={{ ...itemStyle, color: "#94a3b8" }}
                 onClick={() => pick({ createNew: false, clientId: "", clientName: "" })}>
              — ללא שיוך (דלג) —
            </div>
            {taz && (
              <div style={{ ...itemStyle, color: "#0369a1", fontWeight: 700 }}
                   onClick={() => pick({ createNew: true })}>
                🆕 צור לקוח חדש (ת"ז {taz})
              </div>
            )}
            {shown.map(c => (
              <div key={c.clientId} style={itemStyle}
                   onMouseEnter={e => { e.currentTarget.style.background = "#f0f9ff"; }}
                   onMouseLeave={e => { e.currentTarget.style.background = "white"; }}
                   onClick={() => pick({ createNew: false, clientId: c.clientId, clientName: c.clientName })}>
                {c.clientName} <span style={{ color: "#94a3b8", fontFamily: "monospace", fontSize: 10, direction: "ltr" }}>({c.clientId})</span>
              </div>
            ))}
            {filtered.length > shown.length && (
              <div style={{ padding: "6px 10px", fontSize: 10, color: "#94a3b8" }}>
                מוצגים {shown.length} מתוך {filtered.length} — חדד את החיפוש
              </div>
            )}
            {filtered.length === 0 && (
              <div style={{ padding: "10px", fontSize: 11, color: "#94a3b8", textAlign: "center" }}>
                לא נמצא לקוח תואם
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── שלב 1: העלאה ─────────────────────────────────────────────
function UploadStep({ onFiles, busy, error }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const dragCounter = useRef(0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        onDragEnter={e => { e.preventDefault(); dragCounter.current++; setDragging(true); }}
        onDragLeave={e => { e.preventDefault(); if (--dragCounter.current === 0) setDragging(false); }}
        onDragOver={e => e.preventDefault()}
        onDrop={e => {
          e.preventDefault();
          dragCounter.current = 0;
          setDragging(false);
          if (e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
        }}
        style={{
          border: `2px dashed ${dragging ? "#0284c7" : "#cbd5e1"}`,
          borderRadius: 12, padding: "40px 24px",
          background: dragging ? "#f0f9ff" : "#f8fafc",
          display: "flex", flexDirection: "column",
          alignItems: "center", gap: 10, cursor: "default",
          transition: "all 0.15s",
        }}
      >
        <div style={{ fontSize: 48 }}>{dragging ? "📂" : "📈"}</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: dragging ? "#0284c7" : "#475569" }}>
          {dragging ? "שחרר כאן" : "גרור דוחות תיק מנוהל (Excel)"}
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8", textAlign: "center", lineHeight: 1.6 }}>
          דוחות חודשיים מבתי השקעות · מור, ילין לפידות
          <br />
          בית ההשקעות מזוהה אוטומטית מתוך הקובץ
        </div>
      </div>

      <button
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        style={{
          padding: "11px 0", background: busy ? "#bae6fd" : "#0284c7",
          color: "white", border: "none", borderRadius: 8,
          cursor: busy ? "wait" : "pointer", fontWeight: 700, fontSize: 14,
        }}
      >
        {busy ? "⏳ מנתח…" : "בחר קבצים מהמחשב"}
      </button>

      {error && (
        <div style={{ fontSize: 12, padding: "10px 12px", borderRadius: 8, background: "#fef2f2", color: "#991b1b" }}>
          ⚠️ {error}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".xls,.xlsx"
        multiple
        style={{ display: "none" }}
        onChange={e => { if (e.target.files?.length) onFiles(e.target.files); e.target.value = ""; }}
      />
    </div>
  );
}

// ── שלב 2: סקירה ושיוך ───────────────────────────────────────
function ReviewStep({ results, houses, holdingsClients, onUpdate, onConfirm, onBack }) {
  const allMonthsSet = results.every(r => r.error || r.month);
  const allHousesSet = results.every(r => r.error || r.house);
  const anyAssigned = results.some(r => !r.error && r.accounts.some(a => a.match.clientId || a.createNew));
  const ready = allMonthsSet && allHousesSet && anyAssigned;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, maxHeight: "70vh", overflow: "hidden" }}>
      <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 20 }}>
        {results.map((res, ri) => {
          if (res.error) return (
            <div key={ri} style={{ background: "#fef2f2", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#dc2626" }}>
              ❌ {res.fileName}: {res.error}
            </div>
          );

          return (
            <div key={ri} style={{ border: "1px solid #e0f2fe", borderRadius: 10, padding: 12 }}>
              {/* כותרת קובץ: בורר בית השקעות + בורר חודש */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: res.house ? "#475569" : "#dc2626" }}>
                    📈 בית השקעות{!res.house && " (חובה)"}:
                  </label>
                  <select
                    value={res.house}
                    onChange={e => onUpdate(ri, null, { house: e.target.value })}
                    style={{
                      padding: "4px 8px", fontSize: 12, borderRadius: 6, fontWeight: 700, color: "#0369a1",
                      border: `1px solid ${res.house ? "#cbd5e1" : "#fca5a5"}`,
                    }}
                  >
                    <option value="">— בחר —</option>
                    {houses.map(h => (
                      <option key={h.key} value={h.key}>{h.name}</option>
                    ))}
                  </select>
                  {res.house && res.house === res.suggestedHouse && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#059669" }}>זוהה מהקובץ</span>
                  )}
                </span>
                <span style={{ fontSize: 11, color: "#64748b" }}>{res.fileName} · {res.accounts.length} תיקים</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, marginRight: "auto" }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: res.month ? "#475569" : "#dc2626" }}>
                    חודש הדוח{!res.month && " (חובה)"}:
                  </label>
                  <input
                    type="month"
                    value={res.month}
                    onChange={e => onUpdate(ri, null, { month: e.target.value })}
                    style={{
                      padding: "4px 8px", fontSize: 12, borderRadius: 6,
                      border: `1px solid ${res.month ? "#cbd5e1" : "#fca5a5"}`,
                    }}
                  />
                </span>
              </div>

              {/* תיקים */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {res.accounts.map((a, ai) => {
                  const how = a.match.how && !a.touched ? HOW_LABEL[a.match.how] : null;
                  return (
                    <div key={ai} style={{ background: "#f8fafc", borderRadius: 8, padding: "8px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
                          תיק {a.portfolioNum} · {a.accountName}
                        </span>
                        {a.taz && <span style={{ fontSize: 10, color: "#64748b", fontFamily: "monospace", direction: "ltr" }}>ת"ז {a.taz}</span>}
                        <span style={{ fontSize: 11, color: "#0369a1", fontWeight: 700, direction: "ltr", marginRight: "auto" }}>₪{fmt(a.totalValue)}</span>
                      </div>
                      <div style={{ fontSize: 10, color: "#64748b" }}>
                        {a.policyLabel}
                        {a.dmeiNihulPct != null && <> · דמי ניהול: {a.dmeiNihulPct}%</>}
                        {a.maslulimPreview && <> · {a.maslulimPreview}</>}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <ClientPicker
                          value={a.match.clientId}
                          createNew={a.createNew}
                          taz={a.taz}
                          clients={holdingsClients}
                          onPick={p => {
                            if (p.createNew) {
                              onUpdate(ri, ai, { createNew: true, newName: a.accountName, match: { clientId: "", clientName: "", how: null }, touched: true });
                            } else {
                              onUpdate(ri, ai, { createNew: false, match: { clientId: p.clientId, clientName: p.clientName || "", how: a.match.how }, touched: p.clientId !== a.match.clientId ? true : a.touched });
                            }
                          }}
                        />
                        {a.createNew && (
                          <input
                            value={a.newName}
                            onChange={e => onUpdate(ri, ai, { newName: e.target.value })}
                            placeholder="שם הלקוח החדש"
                            style={{ padding: "4px 8px", fontSize: 12, borderRadius: 6, border: "1px solid #cbd5e1", width: 160 }}
                          />
                        )}
                        {how && <span style={{ fontSize: 10, fontWeight: 700, color: how.color }}>{how.text}</span>}
                        {!a.match.clientId && !a.createNew && (
                          <span style={{ fontSize: 10, color: "#94a3b8" }}>התיק לא ייקלט ללא שיוך</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
        <button onClick={onBack} style={{ padding: "10px 20px", background: "white", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13, color: "#64748b" }}>
          חזור
        </button>
        <button
          onClick={onConfirm}
          disabled={!ready}
          title={!allHousesSet ? "יש לבחור בית השקעות לכל קובץ" : !allMonthsSet ? "יש לבחור חודש דוח לכל קובץ" : !anyAssigned ? "אין אף תיק משויך" : undefined}
          style={{
            flex: 1, padding: "10px 0",
            background: ready ? "#0284c7" : "#cbd5e1",
            color: "white", border: "none", borderRadius: 8,
            cursor: ready ? "pointer" : "not-allowed",
            fontWeight: 700, fontSize: 14,
          }}
        >
          ✓ אשר ייבוא
        </button>
      </div>
    </div>
  );
}

// ── שלב 3: סיום ──────────────────────────────────────────────
function DoneStep({ summary, onClose }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: "16px 0" }}>
      <div style={{ fontSize: 48 }}>✅</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: "#0369a1" }}>הייבוא הושלם</div>
      <div style={{ fontSize: 13, color: "#475569", textAlign: "center" }}>
        {summary.linked > 0 && <div>✓ {summary.linked} תיקים שויכו ללקוחות</div>}
        {summary.created > 0 && <div>🆕 {summary.created} לקוחות חדשים נוצרו</div>}
        {summary.skipped > 0 && <div style={{ color: "#f59e0b" }}>◌ {summary.skipped} תיקים דולגו (ללא שיוך)</div>}
      </div>
      <button onClick={onClose} style={{ padding: "10px 32px", background: "#0284c7", color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 14 }}>
        סגור
      </button>
    </div>
  );
}

// ── המודל ────────────────────────────────────────────────────
export default function ManagedPortfolioImport({ onClose, holdingsClients, addHoldingsFunds, initialFiles }) {
  const [step, setStep] = useState("upload");
  const [results, setResults] = useState([]);
  const [houses, setHouses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleFiles = async (files) => {
    setBusy(true);
    setError(null);
    const { parseManagedPortfolioFile, MANAGED_HOUSES } = await loadParser();
    setHouses(MANAGED_HOUSES);
    const out = [];
    for (const file of Array.from(files)) {
      try {
        const buf = await file.arrayBuffer();
        const parsed = parseManagedPortfolioFile(buf, file.name);
        out.push({
          fileName: file.name,
          format: parsed.format,
          house: parsed.suggestedHouse || "",        // הבחירה בפועל — ניתנת לשינוי במסך הסקירה
          suggestedHouse: parsed.suggestedHouse || "",
          reportDate: parsed.reportDate,
          month: defaultMonth(parsed.reportDate, file.name),
          accounts: parsed.accounts.map(a => {
            // תקציר אפיקים לתצוגה: 3 הגדולים
            const top = [...a.allocations]
              .filter(al => !al.isAggregate && al.value > 0)
              .sort((x, y) => y.value - x.value)
              .slice(0, 3)
              .map(al => al.label)
              .join(", ");
            return {
              ...a,
              policyLabel: /[א-ת]/.test(String(a.policy ?? "")) ? `מדיניות: ${a.policy}` : `תיק מנוהל ${a.portfolioNum}`,
              maslulimPreview: top,
              match: initialMatch(a, parsed.suggestedHouse, holdingsClients),
              createNew: false,
              newName: "",
              touched: false,
            };
          }),
        });
      } catch (e) {
        out.push({ fileName: file.name, error: e.message, accounts: [] });
      }
    }
    setBusy(false);
    if (out.every(r => r.error)) {
      setError(out.map(r => r.error).join(" · "));
      return;
    }
    setResults(out);
    setStep("review");
  };

  useEffect(() => {
    if (initialFiles?.length) handleFiles(initialFiles);
  }, []); // eslint-disable-line

  // עדכון קובץ (ai=null) או חשבון בתוך קובץ
  const handleUpdate = (ri, ai, patch) => {
    setResults(prev => prev.map((r, i) => {
      if (i !== ri) return r;
      if (ai === null) {
        const next = { ...r, ...patch };
        // החלפת בית השקעות משנה את מפתח המיפוי השמור — מריצים שיוך מחדש
        // לחשבונות שלא נגעו בהם ידנית
        if (patch.house !== undefined && patch.house !== r.house) {
          next.accounts = r.accounts.map(a =>
            a.touched || a.createNew ? a : { ...a, match: initialMatch(a, patch.house, holdingsClients) }
          );
        }
        return next;
      }
      return { ...r, accounts: r.accounts.map((a, j) => (j === ai ? { ...a, ...patch } : a)) };
    }));
  };

  const handleConfirm = async () => {
    const { buildManagedFund } = await loadParser(); // כבר בקאש מ-handleFiles
    let linked = 0, created = 0, skipped = 0;

    for (const res of results) {
      if (res.error || !res.month || !res.house) continue;
      const house = houses.find(h => h.key === res.house);
      // היום האחרון בחודש הנבחר — תאריך הערך של ה-snapshot
      const [yy, mm] = res.month.split("-").map(Number);
      const lastDay = new Date(yy, mm, 0).getDate();
      const reportDateISO = `${res.month}-${String(lastDay).padStart(2, "0")}`;

      const funds = [];
      const mappings = [];

      for (const a of res.accounts) {
        let clientId = null, clientName = null;
        if (a.createNew && a.taz && a.newName?.trim()) {
          clientId = a.taz;
          clientName = a.newName.trim();
          created++;
        } else if (a.match.clientId) {
          clientId = a.match.clientId;
          clientName = a.match.clientName;
          linked++;
        } else {
          skipped++;
          continue;
        }
        funds.push(buildManagedFund(a, house, reportDateISO, { id: clientId, name: clientName }));
        mappings.push({ house: house.key, portfolioNum: a.portfolioNum, clientId, clientName });
      }

      if (funds.length) {
        addHoldingsFunds(
          funds,
          {
            fileName: res.fileName,
            companyCode: `MP_${house.key.toUpperCase()}`,
            shemYatzran: house.name,
            taarichBitzua: reportDateISO,
            sourceType: SOURCE_TYPE.MANAGED,
          },
          { replaceExisting: true }
        );
        savePortfolioMappings(mappings);
      }
    }

    setSummary({ linked, created, skipped });
    setStep("done");
  };

  return (
    <div
      onClick={busy ? undefined : onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 4000,
        background: "rgba(15,23,42,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        direction: "rtl",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "white", borderRadius: 16,
          padding: 28, width: step === "review" ? 720 : 460,
          maxWidth: "95vw", maxHeight: "90vh",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          display: "flex", flexDirection: "column", gap: 16,
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <button onClick={onClose} disabled={busy} style={{ background: "transparent", border: "none", fontSize: 18, cursor: busy ? "default" : "pointer", color: "#94a3b8" }}>
            ✕
          </button>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#1e293b" }}>
            📈 ייבוא דוח תיק מנוהל
          </h3>
        </div>

        {step === "upload" && <UploadStep onFiles={handleFiles} busy={busy} error={error} />}
        {step === "review" && (
          <ReviewStep
            results={results}
            houses={houses}
            holdingsClients={holdingsClients}
            onUpdate={handleUpdate}
            onConfirm={handleConfirm}
            onBack={() => setStep("upload")}
          />
        )}
        {step === "done" && <DoneStep summary={summary} onClose={onClose} />}
      </div>
    </div>
  );
}
